import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNextButton,
  CarouselPreviousButton,
  useCarousel,
} from '@lastbrand/web-ui/carousel'
import { ChevronLeftIcon, ChevronRightIcon } from '@lastbrand/web-ui/icons'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { CarouselFocusClipRegion, carouselFocusClipViewportCss } from '@src/ds/blocks/carousel-focus-clip'
import { useCarouselEdgeOverflow } from '@src/ds/hooks/use-carousel-edge-overflow'
import { SubCollection } from '@src/pages/[...slug]/components/collection/domains/Collection'

import DesktopSubCollectionChip, { normalizeImageUrl } from './sub-collection-chip-desktop'

const SUBCOLLECTION_FILTER_ARIA_LABEL = 'subcollection_filter_aria_label'
const DESKTOP_CHIP_IMAGE_CENTER_OFFSET = '72px'
// Figma DS 2.0 node 1223:21559 — GradientLeft/GradientRight are 104px wide, not 80px. The
// rendered box is 1px wider: each fade overhangs the clip boundary by 1px on its anchored side
// (`left`/`right: -1px` below), because at fractional browser zoom the clip edge and the fade
// rasterize to device pixels independently and a hairline of un-faded slide paint showed at
// the very edge the fade exists to whiten (reported at ~1890px viewport, reproduced at 110%
// zoom). The overhang itself is invisible — the gradient's solid-white end over the white page.
const EDGE_FADE_WIDTH = '104px'
const EDGE_FADE_RIGHT =
  'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #FFF 100%), linear-gradient(270deg, #FFF 0%, rgba(255, 255, 255, 0) 100%)'
const EDGE_FADE_LEFT =
  'linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, #FFF 100%), linear-gradient(90deg, #FFF 0%, rgba(255, 255, 255, 0) 100%)'

interface DesktopSubCollectionCarouselProps {
  subCollections: SubCollection[]
  isSelected: (subCollection: SubCollection) => boolean
  onChipClick: (subCollection: SubCollection) => void
  getLabel: (subCollection: SubCollection) => string
}

const carouselControlButtonCss = css.raw({
  position: 'absolute',
  top: DESKTOP_CHIP_IMAGE_CENTER_OFFSET,
  transform: 'translateY(-50%)',
  zIndex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'space-8',
  borderRadius: 'radius-round',
  bg: 'white',
  boxShadow: 'shadow-below',
  color: 'darkgray',
  transition: 'color 150ms ease-out, background-color 150ms ease-out',
  _hover: {
    bg: 'paperwhite',
  },
  _active: {
    bg: 'offwhite',
  },
  _focusVisible: {
    outlineWidth: '2px',
    outlineOffset: '0.25rem',
    outlineColor: 'black',
    outlineStyle: 'solid',
  },
})

/**
 * The cross-fade is deliberately asymmetric (STRFRNT-15364).
 *
 * A fade appears the *instant* its side gains scrolled-out slides: `CarouselFocusClipRegion`
 * closes that side's clip in the same frame, so any fade-in duration leaves the freshly
 * clipped edge sharp and un-faded for its length — the "quick non-faded area" seen while
 * scrolling. Only the removal is animated, which is safe because by then that side has
 * nothing left to hide.
 *
 * See `useCarouselEdgeOverflow` for why the visibility itself is position-derived.
 */
const edgeFadeCss = css.raw({
  position: 'absolute',
  insetY: 0,
  width: EDGE_FADE_WIDTH,
  pointerEvents: 'none',
  opacity: 1,
  transition: 'none',
  '&[data-visible="false"]': {
    opacity: 0,
    transition: 'opacity 250ms ease-out',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&[data-visible="false"]': {
      transition: 'none',
    },
  },
})

const edgeFadeLeftClass = css(edgeFadeCss, { left: '-1px', background: EDGE_FADE_LEFT })
const edgeFadeRightClass = css(edgeFadeCss, { right: '-1px', background: EDGE_FADE_RIGHT })

// How close (in px of track travel) to a limit counts as arrived, for the fades. Embla's
// easing crawls sub-pixel for ~450ms after motion visibly stops; at 8px out the track already
// reads as parked, so a blur leaves the moment the slide looks finished instead of lingering
// over a stationary edge through the invisible tail. The focus clip keeps the hook's strict
// default — un-clipping a side this early would leak up to 8px of slide past the section edge.
const EDGE_FADE_PROXIMITY_PX = 8

function DesktopSubCollectionCarouselControls() {
  const { canScrollNext, canScrollPrev } = useCarousel()
  const { hasPrevOverflow, hasNextOverflow } = useCarouselEdgeOverflow(EDGE_FADE_PROXIMITY_PX)

  return (
    <>
      <div
        aria-hidden="true"
        className={edgeFadeLeftClass}
        data-visible={hasPrevOverflow}
        data-testid={TEST_LOCATORS.SUB_COLLECTION.CAROUSEL_DESKTOP_FADE_LEFT}
      />
      <div
        aria-hidden="true"
        className={edgeFadeRightClass}
        data-visible={hasNextOverflow}
        data-testid={TEST_LOCATORS.SUB_COLLECTION.CAROUSEL_DESKTOP_FADE_RIGHT}
      />
      {canScrollPrev && (
        <CarouselPreviousButton asChild css={{ ...carouselControlButtonCss, left: 0 }}>
          <button type="button">
            <ChevronLeftIcon width="20px" height="20px" />
          </button>
        </CarouselPreviousButton>
      )}
      {canScrollNext && (
        <CarouselNextButton asChild css={{ ...carouselControlButtonCss, right: 0 }}>
          <button type="button">
            <ChevronRightIcon width="20px" height="20px" />
          </button>
        </CarouselNextButton>
      )}
    </>
  )
}
export default function DesktopSubCollectionCarousel({
  subCollections,
  isSelected,
  onChipClick,
  getLabel,
}: DesktopSubCollectionCarouselProps) {
  const { t } = useTranslation('common')

  return (
    <Carousel
      aria-label={t(SUBCOLLECTION_FILTER_ARIA_LABEL)}
      data-testid={TEST_LOCATORS.SUB_COLLECTION.CAROUSEL_DESKTOP}
      opts={{ align: 'start', containScroll: 'trimSnaps', slidesToScroll: 4 }}
      css={{ width: 'full', position: 'relative', border: 'none' }}
    >
      <CarouselFocusClipRegion>
        <CarouselContent viewportCss={carouselFocusClipViewportCss} css={{ gap: 'space-16' }}>
          {subCollections.map(sc => (
            <CarouselItem key={sc.id} css={{ flex: '0 0 auto' }}>
              <DesktopSubCollectionChip
                imageUrl={normalizeImageUrl(sc.imageDesktop?.url)}
                label={getLabel(sc)}
                selected={isSelected(sc)}
                href={sc.slug ? sc.slug : undefined}
                data-testid={TEST_LOCATORS.SUB_COLLECTION.CAROUSEL_DESKTOP_TILE(sc.id)}
                onClick={() => {
                  onChipClick(sc)
                }}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </CarouselFocusClipRegion>
      <DesktopSubCollectionCarouselControls />
    </Carousel>
  )
}
